import React, { useState } from 'react';
import { 
  TrendingUp, Battery, Bell, FolderOpen, Shield, Brain,
  Menu, X
} from 'lucide-react';
import MobileDataForecast from './components/tools/MobileDataForecast';
import BatteryHealthPredictor from './components/tools/BatteryHealthPredictor';
import NotificationOverloadDetector from './components/tools/NotificationOverloadDetector';
import DigitalClutterScore from './components/tools/DigitalClutterScore';
import InternetRiskIndex from './components/tools/InternetRiskIndex';
import DecisionFatigueCalculator from './components/tools/DecisionFatigueCalculator';
import { motion, AnimatePresence } from 'framer-motion';

const App = () => {
  const [activeTab, setActiveTab] = useState("data");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const tabs = [
    { id: "data", name: "Data Forecast", icon: TrendingUp, color: "bg-blue-500", component: MobileDataForecast, description: "Predict monthly mobile data usage" },
    { id: "battery", name: "Battery Health", icon: Battery, color: "bg-green-500", component: BatteryHealthPredictor, description: "Estimate battery lifespan" },
    { id: "notifications", name: "Notification Overload", icon: Bell, color: "bg-yellow-500", component: NotificationOverloadDetector, description: "Track distracting apps" },
    { id: "clutter", name: "Digital Clutter", icon: FolderOpen, color: "bg-purple-500", component: DigitalClutterScore, description: "Measure workspace clutter" },
    { id: "security", name: "Internet Risk", icon: Shield, color: "bg-red-500", component: InternetRiskIndex, description: "Evaluate online security" },
    { id: "fatigue", name: "Decision Fatigue", icon: Brain, color: "bg-indigo-500", component: DecisionFatigueCalculator, description: "Check mental load level" },
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || MobileDataForecast;
  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-sm z-20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
            <TrendingUp size={20} className="text-white" />
          </div>
          <h1 className="font-bold text-gray-800 text-lg">Digital Wellness</h1>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg bg-gray-100">
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Sidebar for Desktop */}
      <div className="hidden lg:block fixed left-0 top-0 h-full w-72 bg-white shadow-xl z-10">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
              <TrendingUp size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Digital Wellness</h1>
              <p className="text-xs text-gray-400">Dashboard</p>
            </div>
          </div>
          
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === tab.id 
                    ? `${tab.color} text-white shadow-md` 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon size={20} />
                <span className="font-medium">{tab.name}</span>
                {activeTab === tab.id && (
                  <motion.div 
                    layoutId="activeTab"
                    className="ml-auto w-1.5 h-1.5 rounded-full bg-white"
                  />
                )}
              </motion.button>
            ))}
          </nav>

          <div className="absolute bottom-6 left-6 right-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
            <p className="text-xs text-gray-500 text-center">
              Make informed decisions<br />about your digital life
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-white shadow-xl z-40 lg:hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
                      <TrendingUp size={24} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-gray-800">Digital Wellness</h1>
                    </div>
                  </div>
                  <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg bg-gray-100">
                    <X size={18} />
                  </button>
                </div>
                
                <nav className="space-y-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        activeTab === tab.id 
                          ? `${tab.color} text-white` 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <tab.icon size={20} />
                      <span className="font-medium">{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="lg:ml-72">
        {/* Desktop Header */}
        <div className="hidden lg:block bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="px-8 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{currentTab?.name}</h2>
                <p className="text-sm text-gray-500 mt-0.5">{currentTab?.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-xs text-gray-400">Your Digital Wellness</p>
                  <p className="text-sm font-semibold text-gray-700">Dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Content with padding for fixed header */}
        <div className="pt-16 lg:pt-0">
          <main className="p-4 lg:p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ActiveComponent />
              </motion.div>
            </AnimatePresence>
          </main>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 bg-white mt-8">
          <div className="px-4 lg:px-8 py-4 text-center text-xs text-gray-400">
            Digital Wellness Dashboard — Monitor & Optimize Your Digital Habits
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;